import React, { useState, useEffect } from 'react';
import { X, Download, ZoomIn, ZoomOut } from 'lucide-react';

const PDFViewer = ({ fileUrl, fileName, onClose, token }) => {
  const [scale, setScale] = useState(100);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch PDF with auth token and create blob URL
    const fetchPDF = async () => {
      try {
        const response = await fetch(fileUrl, {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {}
        });

        if (!response.ok) {
          throw new Error('Failed to load PDF');
        }

        // Create blob with correct MIME type
        const blob = await response.blob();
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });
        const url = URL.createObjectURL(pdfBlob);
        setBlobUrl(url);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchPDF();

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [fileUrl, token]);

  const handleDownload = () => {
    if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      a.click();
    }
  };

  const zoomIn = () => {
    setScale((prev) => Math.min(200, prev + 10));
  };

  const zoomOut = () => {
    setScale((prev) => Math.max(50, prev - 10));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 100000 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-3 py-2 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <h3 className="text-sm font-semibold text-gray-900 truncate max-w-md">{fileName}</h3>
          </div>
          
          {/* Controls */}
          <div className="flex items-center space-x-1">
            {!loading && !error && (
              <>
                <button
                  onClick={zoomOut}
                  disabled={scale <= 50}
                  className="p-1.5 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Zoom Out"
                >
                  <ZoomOut className="h-4 w-4" />
                </button>
                
                <span className="text-xs text-gray-600 min-w-[50px] text-center">
                  {scale}%
                </span>
                
                <button
                  onClick={zoomIn}
                  disabled={scale >= 200}
                  className="p-1.5 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Zoom In"
                >
                  <ZoomIn className="h-4 w-4" />
                </button>
                
                <div className="w-px h-5 bg-gray-300 mx-1" />
              </>
            )}
            
            <button
              onClick={handleDownload}
              disabled={!blobUrl}
              className="p-1.5 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
              title="Download PDF"
            >
              <Download className="h-4 w-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 overflow-hidden bg-gray-100">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mb-4 mx-auto"></div>
                <p className="text-gray-600">Loading PDF...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8">
                <p className="text-lg font-semibold text-red-600 mb-4">Failed to load PDF</p>
                <p className="text-sm text-gray-600 mb-6">{error}</p>
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <iframe
              src={`${blobUrl}#view=FitH`}
              className="w-full h-full"
              title={fileName}
              style={{ border: 'none' }}
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-3 py-2 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-600">
            {error ? 'PDF could not be loaded' : loading ? 'Loading...' : '\u00A0'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
